// Cross-panel reactive state. Used in every view so the window selector
// and "include test sessions" toggle stay in sync without prop drilling.

import { signal } from "@preact/signals";
import type { Signal } from "@preact/signals";
import type { WindowLabel } from "@/api/types";

export const includeTestSessions = signal<boolean>(false);

// Partner scope. "" = all traffic (the default and the pre-existing
// behaviour). Otherwise a tenant id from GET /admin/api/tenants — including
// the two built-in buckets `unknown` and `unmapped`. Global rather than
// per-view: comparing Cost and Quality for the same partner is the point, so
// switching views must not silently drop the scope.
export const selectedTenant = signal<string>("");
export const selectedWindow = signal<WindowLabel>("24h");
export const peakWindow = signal<WindowLabel>("7d");
export const costWindow = signal<WindowLabel>("7d");
export const signalsWindow = signal<WindowLabel>("7d");
export const kpiWindow = signal<WindowLabel>("7d");
export const talentGeoWindow = signal<WindowLabel>("30d");
// A partner report is a billing-period artefact, so it defaults wider than
// the operational panels.
export const partnerWindow = signal<WindowLabel>("30d");
// The analysis chat reads at most one read's worth of turns, so its windows
// stay short.
export const analystWindow = signal<WindowLabel>("7d");

// The dashboard views. Lifted out of App.tsx so other views (Events Stream,
// Recent Q&A) can switch the active view programmatically; the list is also
// what a URL is checked against.
export const VIEW_IDS = [
  "health",
  "ops",
  "customers",
  "testpage",
  "kpi",
  "pulse",
  "engagement",
  "peak",
  "quality",
  "signals",
  "questions",
  "talentgeo",
  "cost",
  "estimate",
  "partner",
  "aggregates",
  "qna",
  "sessions",
  "events",
  "analyst",
  "insights",
  "prompts",
  "config",
  "glossary",
] as const;

export type ViewId = (typeof VIEW_IDS)[number];

export const isViewId = (value: string): value is ViewId =>
  (VIEW_IDS as readonly string[]).includes(value);

export const currentView = signal<ViewId>("health");

// Which window signal each view reads, and the windows it may offer. The
// selector lives in the global header, so this map is what tells the header
// whether to render it at all and what to bind it to. A view absent here has
// no time window (Weekly Aggregates is week-bucketed; the estimate is always
// the last 30 days; «Betrieb» shows today and the last 30 Zurich days; the
// Fragen-Check works on its pasted list; the Tagesbericht is one Zurich day
// per report; the inspectors, Config and the Glossary are list/point views).
//
// Views that SHARE a signal must offer the same options — otherwise switching
// between them leaves the shared value outside the new list and no pill reads
// as active.
export interface ViewWindow {
  signal: Signal<WindowLabel>;
  options: WindowLabel[];
}

const TREND: WindowLabel[] = ["24h", "7d", "90d", "180d", "365d"];
const SHORT: WindowLabel[] = ["24h", "7d", "30d"];
// Talent-Geografie defaults to 30d, so its list has to contain 30d.
const TREND_WITH_30D: WindowLabel[] = ["24h", "7d", "30d", "90d", "180d", "365d"];

export const VIEW_WINDOWS: Partial<Record<ViewId, ViewWindow>> = {
  health: { signal: selectedWindow, options: TREND },
  kpi: { signal: kpiWindow, options: TREND },
  pulse: { signal: selectedWindow, options: TREND },
  engagement: { signal: selectedWindow, options: TREND },
  peak: { signal: peakWindow, options: SHORT },
  quality: { signal: selectedWindow, options: TREND },
  signals: { signal: signalsWindow, options: TREND },
  talentgeo: { signal: talentGeoWindow, options: TREND_WITH_30D },
  cost: { signal: costWindow, options: SHORT },
  partner: { signal: partnerWindow, options: TREND_WITH_30D },
  analyst: { signal: analystWindow, options: SHORT },
};

// Views that read `selectedTenant`. The header only renders the tenant
// selector for these — showing it above the Config or Prompts view would
// imply a scope that isn't applied. Partner-Report is absent on purpose: it
// owns its own tenant picker because a tenant is mandatory there, not a filter.
export const TENANT_SCOPED_VIEWS: ReadonlySet<ViewId> = new Set<ViewId>([
  "health",
  "kpi",
  "pulse",
  "engagement",
  "peak",
  "quality",
  "signals",
  "talentgeo",
  "cost",
  "estimate",
  "analyst",
]);

// Cross-view jump target: a session_id the Events Stream wants the Session
// Inspector to load. Set together with `currentView = "sessions"`; the
// inspector consumes it (pre-fills + loads) and clears it.
export const inspectSessionId = signal<string>("");

/** Opens a conversation in the Session Inspector. */
export function openInInspector(sessionId: string): void {
  inspectSessionId.value = sessionId;
  currentView.value = "sessions";
}

// The Fragen-Check the analysis chat discusses instead of the header's chat
// data. Set by the Fragen-Check together with `currentView = "analyst"`; the
// chat's «zurück zu den Chatdaten» clears it.
export interface AnalystCheckScope {
  id: string;
  name: string;
  questions: number;
  createdAt: string;
}

export const analystCheck = signal<AnalystCheckScope | null>(null);
